import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux';

import { updateWidgetContent, toggleEditMode, updateWidgetProperty, splitPageBySelection } from "actions/editor";

import _ from 'lodash';
import cx from 'classnames';
import { Frame } from "scenejs";
import { IKImage } from "imagekitio-react";

import { EditorContext } from '../../index';
import { BackgroundView } from '../background';
import { Property } from '../../components/properties/';
import { Animation, AnimationProperty } from 'components/Editor/components/animations';
import { AssetManagerModal } from 'components/AssetManager';
import WidgetActions from 'components/Editor/components/properties/widgetActions';
import { MediaLibraryModal } from 'components/MediaLibrary';
import { Background } from 'components/Editor/components/properties/background';
import StoryboardContext from 'components/Storyboard/context';
import { getBoundingRectFromLayout } from 'components/Editor/helper/layoutManager';
import { useContainerSelector, usePageWidgetSelector, useSelectedWidgetSelector } from 'components/Editor/hooks/useEditorSession';
import { getParentContainerOfType, getWidgetType, isWidgetHasHotspotChild } from 'components/Editor/helper/widget';

import FileService from 'services/file.service.jsx';

import { CONTENT_CARD, GROUP, SECTION_COLUMN, PAGE as PAGE_CONTAINER, CAROUSEL_CARD } from 'constants/widgetTypes';
import { FREE, PAGE, STRETCHED } from 'constants/widgetDisplayTypes';
import { DEFAULT, DOCUMENT, VIDEO } from 'constants/widgetAssetTypes';

import { getMediaInfo } from 'actions/pageHealth';
import { useMakeFrame } from 'components/Editor/hooks/useMakeFrame';
import { INTERACTIVE_CONTENT_TYPES, PAGE_CONTAIN_STORYBOARDS, SLIDE_STORYBOARDS } from 'constants/storyboardType';
import styles from './asset.module.scss';

const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;

const defaultContentImage = '/public/cleverstory/v2/static/default_card.jpg';

const state = {
  displayType: PAGE,
  breakPointType: 'desktop'
};

const updateState = (widget, storyboardType) => {
  state.displayType = SLIDE_STORYBOARDS.includes(storyboardType) ? FREE : _.get(widget, 'properties.desktop.display', PAGE);
};
const updateBreakPoint = (breakpoint) => {
  state.breakPointType = breakpoint;
}

const AssetView = function AssetWidget({ className, style, widgetProps, widgetRef, children }) {
  const dispatch = useDispatch();
  const [allowType, setAllowType] = useState(null)
  const ref = widgetRef;

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;
  const editorType = _.get(editorContext, 'entityType');
  const storyboardContext = React.useContext(StoryboardContext);
  const storyboardType = _.get(storyboardContext, 'type');
  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');
  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));
  const isExternalVideo = checkExternalVideoType(widgetProps);
  const widgetSelected = useSelectedWidgetSelector(editor);
  const container = useContainerSelector(editor, id);

  const display = _.get(widgetProps, 'properties.desktop.display', PAGE);
  const contentType = _.get(widgetProps, 'properties.desktop.type');
  const currentFileType = _.get(widgetSelected[0], 'properties.desktop.type');

  const content = _.get(widgetProps, 'content.content', null) || _.get(widgetProps, 'content', null);

  const background = _.get(widgetProps, 'properties.desktop.background', null);
  const poster = _.get(widgetProps, 'properties.desktop.video.poster', null);
  const translate = _.get(widgetProps, 'properties.desktop.layout.transform.translate', "");
  const y = translate.split(',')[1];

  useEffect(() => {

    if (widgetSelected) {
      updateState(widgetSelected[0], storyboardType);
      setAllowType(currentFileType);
    }

    if (breakpoint) {
      updateBreakPoint(breakpoint)
    }
    
  }, [widgetSelected, breakpoint])

  const editMode = useSelector((state) => {
    return _.get(state, `${editor}.present.editMode`) === id;
  });

  var assetWidgetStyle = {
    ...style,
    ...getStyles(widgetProps, breakpoint, container)
  }

  const handleSelectAsset = (assets) => {
    let assetContent = assets[0];
    let metaData = _.get(assetContent, 'asset.metadata', null);

    let contentRequest = {
      uid: `asset_${assetContent.id}`,
      type: 'ASSET',
      content: assetContent
    }

    dispatch(updateWidgetContent(editor, id, contentRequest));

    let widgetContentType = FileService.fileContentType(metaData);
    const assetWidth = _.get(widgetProps, `properties.${breakpoint}.layout.width`);
    var request = {
      type: widgetContentType,
    }

    if(SLIDE_STORYBOARDS.includes(storyboardType) && breakpoint === 'desktop' && !assetWidth) {
      request = {
        ...request,
        layout: {
          ...widgetProps.properties[breakpoint].layout,
          width: '1200px'
        },
      }
    }

    if (widgetContentType != contentType) {
      request = {
        ...request,
        layout: {
          ...widgetProps.properties[breakpoint].layout,
          height: '400px'
        },
        display: state.displayType
      }
    }

    if (widgetContentType === "DOCUMENT" && widgetContentType != contentType) {
      request = {
        ...request,
        layout: {
          ...widgetProps.properties[breakpoint].layout,
          transform: {
            ...widgetProps.properties[breakpoint].layout.transform,
            translate: `0px, ${y}`
          },
          width: '1200px'
        }
      }
    }
 
    dispatch(updateWidgetProperty(editor, id, breakpoint, request));
    dispatch(toggleEditMode(editor, id, false));
  }

  const handleCloseAssetManager = () => {
    dispatch(toggleEditMode(editor, id, false));
  }

  return (
    <>
      <div
        id={id}
        ref={ref}
        className={cx(className, styles.inlineAsset, 'widget', {
          [styles.stretched]: display === STRETCHED,
          [styles.mobile]: breakpoint === 'mobile'
        })}
        style={assetWidgetStyle}
        data-widget-id={id}
        data-widget-type={type}
        data-selectable={true}
        data-display-type={display}
        data-asset={_.get(content, 'id', null)}
      >
        <Animation widget={widgetProps}>
          <>
            {(contentType === VIDEO && !isExternalVideo) &&
              <BackgroundView background={{
                type: 'image',
                media: poster
              }} className={styles.videoPoster}/>
            }
            {contentType !== VIDEO && 
              <BackgroundView background={background} />
            }
            {!content && !_.includes(["palette_component"], editorType) &&
              <EmptyMessage contentType={contentType} editor={editor} id={id} setAllowType={setAllowType}/>
            }
            {content &&
              <AssetContentViewer 
                id={id}
                content={content} 
                contentType={contentType}
              />
            }
          </>
        </Animation>

        {editMode && 
          <AssetManagerModal
            show={true}
            onFinish={handleSelectAsset}
            onClose={handleCloseAssetManager}
            widgetProps={widgetProps}
            disableCleverstory={true}
            inline={true}
            allowType = {allowType}
          />
        }
      </div>
    </>
  );
};

const AssetContentViewer = ({ id, content, contentType }) => {
  switch (contentType) {
    case DOCUMENT: {
      return <DocumentRender id={id} content={content} />
    }
    case VIDEO: {
      return <VideoRender id={id} content={content}/>
    }
    default: {
      return <DefaultRender id={id} content={content}/>
    }
  }
}

const DocumentRender = ({ id, content }) => {
  var iconUrl = _.get(content, 'asset.icon.key', null);

  return (
    <div className={styles.documentViewer}>
        <div className={styles.documentViewerInner}>
          <IKImage
            id={`asset_icon_${id}`}
            className={styles.icon}
            path={iconUrl || defaultContentImage}
          />
        </div>
    </div>
  )
}

const VideoRender = ({ id, content }) => {
  var iconUrl = _.get(content, 'asset.icon.key', null);

  return (
    <div className={styles.videoViewer}>
      <div className={styles.videoViewerInner}>
        <IKImage
          id={`asset_icon_${id}`}
          className={styles.icon}
          path={iconUrl || defaultContentImage}
        />
      </div>
    </div>
  )
}

const DefaultRender = ({ id, content }) => {
  var iconUrl = _.get(content, 'asset.icon.key', null);

  return (
    <div id={id} className={cx(styles.defaultViewer)}>
      <div className={styles.inner}>
        <div className={styles.icon}>
          <IKImage path={iconUrl || defaultContentImage} width={400} height={226.75} />
        </div>
        <div className={styles.message}>Unable to preview the asset</div>
      </div>
    </div>
  )
}


const EmptyMessage = ({ contentType, editor, id, setAllowType}) => {
  const dispatch = useDispatch();

  const handleClick = () => {
    setAllowType(contentType)
    dispatch(toggleEditMode(editor, id, true));
  }
  
  return (
    <div className={styles.contentEmptyMessage} onClick={handleClick}>
      <div className={styles.emptyMessageInner}>
        <i className={cx('cleverstory-video-inline', styles.emptyMessageIcon)}></i>
        <span className={styles.emptyMessageLabel}>
          <i className={cx('cleverstory-plus')}></i>&nbsp;
          Choose&nbsp;
          <span className={styles.contentType}>{`${contentType && contentType.charAt(0).toUpperCase()}${contentType && contentType.slice(1).toLowerCase() }`}</span>
        </span>
      </div>
    </div>
  )
}

const AssetWidget = React.forwardRef((props, ref) => {
  return <AssetView {...props} ref={ref} />
});

const AssetWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();
  
  const storyboardContext = React.useContext(StoryboardContext);

  const [view, setView] = useState(null);

  const id = _.get(props, 'uid');
  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const editorType = _.get(editorContext, 'entityType');

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const isExternalVideo = checkExternalVideoType(props);

  const container = useContainerSelector(editorSession, id);
  const contentType = _.get(props, 'properties.desktop.type', DEFAULT);
  const background = _.get(props, 'properties.desktop.background');
  const display = _.get(props, 'properties.desktop.display', PAGE);
  const fullHeight = _.get(props, 'properties.desktop.fullHeight', false);

  const properties = _.get(props, 'properties.desktop', {});
  const layout = _.get(props, `properties.${breakpoint}.layout`);

  const pageWidget = usePageWidgetSelector(editorSession, id);
  const pageWidth = _.get(pageWidget, 'properties.desktop.container.minWidth');

  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const { makeFrameShow, makeFramePageId } = useMakeFrame({ editorSession });

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const handleUpdateImage = (images) => {
    let poster = images[0];
    dispatch(updateWidgetProperty(editorSession, props.uid, 'desktop', {
      video: {
        ...properties.video,
        poster: poster 
      }
    }));
    closePopup()
  }

  useEffect(() => {
    setView(null);
  },[id])

  const handleDisplayChange = (display) => {
    var frame = new Frame(layout);
    const rect = getBoundingRectFromLayout(layout);

    frame.set('transform', 'translate', `0px, ${rect.y}px`);
    if(display === FREE) {
      frame.set('transform', 'translate', `${rect.x}px, ${rect.y}px`);
      frame.set('width', `${pageWidth}px`);
    }

    if(display === PAGE) {
      frame.set('width', `${pageWidth}px`);
    }

    if(display === STRETCHED) {
      frame.remove('width');
    }

    var request = {
      display: display,
      layout: frame.get()
    }

    dispatch(updateWidgetProperty(editorSession, props.uid, 'desktop', request));
  }

  const handleHeightChange = (fullHeight) => {
    var frame = new Frame(layout);
    const rect = getBoundingRectFromLayout(layout);

    if(fullHeight) {
      const assetIconElement = document.getElementById(`asset_icon_${props.uid}`);

      if (assetIconElement) {
        const assetIconRect = assetIconElement.getBoundingClientRect();
        const height = Math.max(isNaN(assetIconRect.height) ? 0 : assetIconRect.height, 400)
        frame.set('height', `${height}px`);
      }
    } else {
      const height = Math.max(isNaN(rect.height) ? 0 : rect.height, 400);
      frame.set('height', `${height}px`);
    }

    var request = {
      fullHeight: fullHeight,
      layout: frame.get()
    }
    
    dispatch(updateWidgetProperty(editorSession, props.uid, 'desktop', request));
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  const handleMakeFrame = () => {
    dispatch(splitPageBySelection(editorSession, makeFramePageId, [props.uid]));
  };

  return (
    <>
      { !_.includes(["palette_component"], editorType) &&
         <Property.Action className={styles.textButton} onClick={() => {
          dispatch(toggleEditMode(editorSession, props.uid, true));
        }}>Choose Content</Property.Action>
      }

      {(contentType === VIDEO && !isExternalVideo) &&
        <>
          <WidgetActions.Divider />
          <WidgetActions.Action className={styles.textButton} onClick={() => changeView('poster')}>Change Poster</WidgetActions.Action>
        </>
      } 

      {makeFrameShow && 
        <>
          <Property.Divider />
          <Property.Action className={styles.textButton} tooltip='Split segment: selection becomes a new frame' onClick={handleMakeFrame}>
            Make Frame
          </Property.Action>
        </>
      }

      {contentType !== VIDEO && 
        <Property.Action className={styles.textButton} onClick={() => changeView('background')}>
          Change Background
        </Property.Action>
      }

      {!fullHeight && 
        <>
          <WidgetActions.Divider />
          <Property.Action className={styles.iconButton} tooltip='Animation' onClick={() => changeView('animation')}>
            <i className={cx("cleverstory-animations")} />
          </Property.Action>
        </>
      }

      {_.get(container, 'type') === PAGE_CONTAINER &&
        <>
        <WidgetActions.Divider />
          <Property.Action className={styles.iconButton} tooltip='Stretch' onClick={() => changeView('stretch')}>
            <i className={cx("cleverstory-stretch")} />
          </Property.Action>
        </>
      }

      <WidgetActions.Divider />

      <Property.Action className={styles.iconButton} tooltip='Help' onClick={() => { window.open(`${process.env.REACT_APP_CLEVERSTORY_SUPPORT}`, '_blank') }}>
        <i className={'cleverstory-help'} />
      </Property.Action>

      <MediaLibraryModal
        multiple={false}
        show={view === 'poster'}
        storyboard={storyboardContext}
        mediaTypes={['image']}
        onSelect={handleUpdateImage}
        onClose={() => { closePopup() }}
      />

      {!editMode && view === 'background' &&
        <Background.Dialog id={id}
          value={background}
          onChange={(value) => {
            dispatch(updateWidgetProperty(editorSession, props.uid, 'desktop', {
              background: value
            }));
          }}
          onCancel={() => {
            closePopup();
          }}
        />
      }

      {!editMode && view === 'animation' &&
        <AnimationProperty
          ids={[id]}
          editor={editorSession}
          editorContext={editorContext}
          widgets={[props]}
          onCancel={() => {
            closePopup()
          }}
          exclusions={['spin', 'parallax']}
        />
      }
      
      {!editMode && view === 'stretch' &&
        <SwitchDisplay
          id={id}
          contentType={contentType}
          display={display}
          fullHeight={fullHeight}
          title={contentType === DOCUMENT ? 'Stretch' : 'Stretch/Width'}
          onChange={handleDisplayChange}
          onHeightChange={handleHeightChange}
          onCancel={() => {
            closePopup()
          }}
        />
      }
    </>
  )
}

function checkExternalVideoType(props) {
  const fields = _.get(props, 'content.fields', []);
  const contentVideoType = fields.find(field => field.name === "contentType")?.content || "";
  
  const metadata = _.get(props, 'content.content.asset.metadata', {});
  const { external_video_provider, external_video_type } = metadata;
  
  const externalProviders = ['vimeo', 'wistia', 'vidyard'];
  const externalTypes = ['video/vimeo', 'video/wistia', 'video/vidyard'];
  const contentTypes = ['VIMEO', 'WISTIA', 'VIDYARD'];
  
  return externalProviders.includes(external_video_provider) ||
         externalTypes.includes(external_video_type) ||
         contentTypes.includes(contentVideoType);
}

const AssetWidgetMobileAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const fullHeight = _.get(props, 'properties.desktop.fullHeight', false);

  const id = _.get(props, 'uid');
  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  return (
    <>
      {!fullHeight && 
        <>
          <Property.Action className={styles.iconButton} tooltip='Animation' onClick={() => changeView('animation')}>
            <i className={'cleverstory-animations'} />
          </Property.Action>
        </>
      }
      <WidgetActions.Divider />
      
      <Property.Action className={styles.iconButton} tooltip='Help' onClick={() => { window.open(`${process.env.REACT_APP_CLEVERSTORY_SUPPORT}`, '_blank') }}>
        <i className={'cleverstory-help'} />
      </Property.Action>

      {!editMode && view === 'animation' &&
        <AnimationProperty
          ids={[id]}
          editor={editorSession}
          editorContext={editorContext}
          widgets={[props]}
          onCancel={() => {
            closePopup()
          }}
          exclusions={['spin', 'parallax']}
        />
      }
    </>
  )
}

const SwitchDisplay = ({ id, contentType, display, title, onChange, fullHeight, onHeightChange, ...rest }) => {

  const storyboardContext = React.useContext(StoryboardContext);
  const storyboardType = _.get(storyboardContext, 'type');

  return (
    <Property.Dialog className={styles.stretchDialog}
      width={230}
      target={`#${id}`}
      title={title || 'Stretch'}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        {!INTERACTIVE_CONTENT_TYPES.includes(storyboardType) &&
          <>
            <Property className={styles.sizeProperty}>
              {contentType === DOCUMENT &&
                <h4 className={styles.subHeading}>Document Width</h4>
              }
              <div className={styles.sizeContainer}>
                <DocWidth title={'Screen'} value={STRETCHED} onChange={onChange} display={display}/>
                <DocWidth title={'Page'} value={PAGE} onChange={onChange} display={display} />
                {contentType !== DOCUMENT &&
                  <DocWidth title={'Free'} value={FREE} onChange={onChange} display={display} />
                }
              </div>
            </Property>
            {contentType === DOCUMENT &&
              <Property.Divider />
            }
          </>
        }
        {INTERACTIVE_CONTENT_TYPES.includes(storyboardType) && contentType !== DOCUMENT &&
          <>
            <Property className={styles.sizeProperty}>          
              <div className={styles.sizeContainer}>
                <DocWidth title={'Page'} value={PAGE} onChange={onChange} display={display} />
                {contentType !== DOCUMENT &&
                  <DocWidth title={'Free'} value={FREE} onChange={onChange} display={display} />
                }
              </div>
            </Property>
          </>
        }
        {contentType === DOCUMENT && 
          <Property className={styles.sizeProperty}>
            {contentType === DOCUMENT &&
              <h4 className={styles.subHeading}>Document Height</h4>
            }
            <div className={styles.sizeContainer}>
              <div className={cx(styles.verticalSelectionContainer, {
                [styles.active]: fullHeight
              })} onClick={() => onHeightChange(true)}>
                <div className={styles.verticalSelection}>
                  <div className={styles.verticalSelectionInner}>
                    <div className={styles.docHeight}>

                    </div>
                  </div>
                </div>
                <label>Document <br/> Length</label>
              </div>
              <div className={cx(styles.verticalSelectionContainer, {
                [styles.active]: !fullHeight
              })} onClick={() => onHeightChange(false)}>
                <div className={styles.verticalSelection}>
                  <div className={styles.verticalSelectionInner}>
                    <div className={cx(styles.docHeight, styles.sectionHeight)}>

                    </div>
                  </div>
                </div>
                <label>Section <br/> Height</label>
              </div>
            </div>
          </Property>
        }
      </Property.Body>
    </Property.Dialog>
  );
}

const DocWidth = ({ title, value, onChange, display }) => {

  return (
    <div className={cx(styles.size,
      { [styles.stretched]: title === 'Screen' },
      { [styles.free]: title === 'Free' },
      { [styles.active]: display === value }
    )} onClick={() => onChange(value)}>
      <div className={styles.icon}>
        <div className={styles.inner}>
          {title === 'Free' &&
            <>
              <div className={styles.pointer1} />
              <div className={styles.pointer2} />
              <div className={styles.pointer3} />
              <div className={styles.pointer4} />
            </>
          }
        </div>
      </div>
      <label>{title}</label>
    </div>
  )
}

AssetWidget.actions = (editor, breakpoint, props) => {
  if (breakpoint === 'desktop') {
    return (
      <AssetWidgetDesktopAction
        editor={editor}
        breakpoint={breakpoint}
        {...props}
      />
    );
  } else if (breakpoint === 'mobile') {
    return (
      <AssetWidgetMobileAction
        editor={editor}
        breakpoint={breakpoint}
        {...props}
      />
    );
  }


  return false;
}

AssetWidget.processRequest = (widget) => {
  var content = _.get(widget, 'content.content.id', null) || _.get(widget, 'content.id', null);
  var background = _.get(widget, 'properties.desktop.background');
  var video = _.get(widget, 'properties.desktop.video');
  var layout = _.get(widget, 'properties.desktop.layout');

  if (typeof layout.width === 'object' && layout.width !== null && Object.keys(layout.width).length === 0) {
    layout.width = null;
  }

  var request = {
    ...widget,
    content: content
  }

  if (!background) {
    return request;
  }

  var media = _.get(background, 'media');
  if (media) {
    background = {
      ...background,
      media: media.id
    }
  }

  var poster = _.get(video, 'poster');
  if(video && poster) {
    video = {
      ...video,
      poster: poster.id
    }
  }

  return {
    ...request,
    properties: {
      ...widget.properties,
      desktop: {
        ...widget.properties.desktop,
        background: background,
        video: video,
        layout: layout
      }
    }
  }
}

AssetWidget.sizeConfig = async (widget, mediaCache) => {
  const url = _.get(widget, 'content.content.asset.viewer.url', '');

  if (!url) {
    return {
      size: 0,
      type: 'image'
    };
  }
  return {
    size:  await getMediaInfo(`${IMAGE_CDN_ENDPOINT}/${url}`, mediaCache) || 0, // MB
    type: 'image'
  };
}

AssetWidget.moveableConfig = (editorSession, widget, breakpoint) => {
  const desktop = breakpoint === 'desktop';
  const { displayType, breakPointType } = state;
  const id = widget && widget.uid ? widget.uid : null;
  const group = getParentContainerOfType(editorSession, id, GROUP);
  const display = displayType === FREE ? FREE : _.get(widget, 'properties.desktop.display', PAGE);
  const fullHeight = _.get(widget, 'properties.desktop.fullHeight', false);
  
  var directions = [];
  if (desktop) {
    if (display === FREE && !fullHeight) {
      directions =  ["n", "s", "ne", "nw", "se", "sw", "e", "w"];
    }

    if (display === FREE && fullHeight) {
      directions = ["e", "w"];
    }

    if (display !== FREE && fullHeight) {
      directions = [];
    }

    if (display !== FREE && !fullHeight) {
      directions = ['n', 's'];
    }
  }

  if (!desktop && !fullHeight) {
    directions =  ['n', 's'];
  } 
  
  if (!desktop && fullHeight) {
    directions =  []
  }
  
  return {
    draggable: true,
    resizable: true,
    rotatable: false,
    cloneable: true,
    deletable: true,
    onDrag: (layout, widget) => {
      const frame = new Frame(layout);
      const widgetElement = document.getElementById(widget);
      const element = document.querySelector('[data-storyboard-type]');
      const storyboardType = element?.getAttribute('data-storyboard-type');
      let display = widgetElement.getAttribute('data-display-type') || PAGE;

      if(breakpoint === 'mobile') {
        return frame.get();
      }

      const translate = frame.get("transform", "translate");
      const values = _.split(translate, ",");

      var x = parseInt(values[0]);
      var y = parseInt(values[1]);

      if (PAGE_CONTAIN_STORYBOARDS.includes(storyboardType) && (displayType !== FREE || breakPointType === 'mobile')){
        y = Math.max(0, y);
      }

      const newTranslate = `${displayType === FREE && breakPointType === 'desktop' ? x : 0}px, ${y}px`;

      frame.set("transform", "translate", newTranslate);
      return frame.get();
    },
    renderDirections: directions,
    onResize: (widget, layout, breakpoint) => {
      const display = _.get(widget, 'properties.desktop.display', PAGE);

      const frame = new Frame(layout);
      if (display === STRETCHED && breakpoint === 'desktop') {
        const margin = _.get(widget, 'properties.desktop.margin', 0);
        const width = `calc(var(--editor-width) - 2 * ${margin}px)`;
        frame.set('width', width);
      }

      return frame.get();
    },

    onReparent: (widget, parent) => {
      const parentWidget = getParentContainerOfType(editorSession, widget, CONTENT_CARD);
    
      const hasHotspotChild = isWidgetHasHotspotChild(editorSession, widget);
      const parentType = getWidgetType(editorSession, parent);
    
      const denyHostpotReparent = hasHotspotChild && ![PAGE_CONTAINER, SECTION_COLUMN].includes(parentType);

      const displayCondition = (displayType === PAGE || displayType === STRETCHED)
    
      if (parentWidget || group || denyHostpotReparent || (displayCondition && parentType !== PAGE_CONTAINER) || breakPointType === "mobile") {
        return false;
      }
    
      return true;
    }
  }
}

function getStyles(widgetProps, breakpoint, container) {
  const display = _.get(widgetProps, 'properties.desktop.display', PAGE);
  const margin = _.get(widgetProps, 'properties.desktop.margin', 0);
  const padding = _.get(widgetProps, `properties.${breakpoint}.padding`, 0);

  const containerProps = _.get(container, `properties.${breakpoint}.container`);
  const containerType = _.get(container, `type`);

  const visibleWidth = _.get(containerProps, `minWidth`);
  const alignment = _.get(containerProps, `alignment`) || 'center';

  var multiplier = 0;
  if (alignment == 'center') {
    multiplier = 0.5;
  } else if (alignment == 'right') {
    multiplier = 1;
  }

  var widgetStyles = {
    marginLeft: containerType !== CAROUSEL_CARD && `calc((100% - ${visibleWidth}px) * ${multiplier})`,
    paddingTop: `${padding}px`,
    paddingBottom: `${padding}px`
  }

  if (breakpoint === 'desktop') {
    widgetStyles = {
      ...widgetStyles,
      paddingLeft: `${padding}px`,
      width: '1200px',
      paddingRight: `${padding}px`
    }

    if (display === STRETCHED) {
      widgetStyles = {
        ...widgetStyles,
        width: `calc(var(--editor-width) - 2 * ${margin}px)`,
        left: `calc((100% - (var(--editor-width) - 2 * ${margin}px))/2)`
      }
    }
  }

  if (breakpoint === 'mobile') {
    widgetStyles = {
      ...widgetStyles,
      width: '100%'
    }
  }

  return widgetStyles;
}

export { AssetWidget };